import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefresh, triggerRefresh } from "@/hooks/useRefresh";
import { peso, fmtDay } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Send, Eye, Sparkles } from "lucide-react";
import { ReorderDialog } from "@/components/ReorderDialog";
import { POCreateDialog, POViewDialog, STATUS_BADGE } from "@/components/PODialogs";

export default function PurchaseOrders() {
  const { auth } = useAuth();
  const canEdit = ["owner", "manager"].includes(auth?.membership?.role);
  const [pos, setPos] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [viewing, setViewing] = useState(null);

  useRefresh(() => {
    api.get("/purchase-orders").then((r) => setPos(r.data));
    api.get("/items").then((r) => setItems(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);

  const sendPO = async (po) => {
    try {
      await api.post(`/purchase-orders/${po.po_id}/send`);
      toast.success(`${po.po_number} sent to supplier`);
      triggerRefresh();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="purchase-orders-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Purchase Orders</h1>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setReordering(true)} data-testid="reorder-suggestions-btn">
              <Sparkles className="h-4 w-4 mr-1" /> Reorder suggestions
            </Button>
            <Button onClick={() => setCreating(true)} data-testid="new-po-btn"><Plus className="h-4 w-4 mr-1" /> New PO</Button>
          </div>
        )}
      </div>
      <div className="border rounded-xl bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr className="border-b">
              <th className="py-2 px-3">PO #</th><th className="py-2 px-3">Supplier</th><th className="py-2 px-3">Branch</th>
              <th className="py-2 px-3">Date</th><th className="py-2 px-3 text-right">Net</th>
              <th className="py-2 px-3 text-right">VAT (12%)</th><th className="py-2 px-3 text-right">Gross</th>
              <th className="py-2 px-3">Status</th><th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {pos.map((p) => (
              <tr key={p.po_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`po-row-${p.po_id}`}>
                <td className="py-2 px-3 font-medium">{p.po_number}</td>
                <td className="py-2 px-3">{p.supplier_name}</td>
                <td className="py-2 px-3 text-muted-foreground">{p.branch_name}</td>
                <td className="py-2 px-3">{fmtDay(p.created_at)}</td>
                <td className="py-2 px-3 text-right">{peso(p.net_total)}</td>
                <td className="py-2 px-3 text-right">{peso(p.vat_total)}</td>
                <td className="py-2 px-3 text-right font-medium">{peso(p.gross_total)}</td>
                <td className="py-2 px-3"><Badge className={STATUS_BADGE[p.status]}>{p.status}</Badge></td>
                <td className="py-2 px-3">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setViewing(p)} data-testid={`view-po-${p.po_id}`}><Eye className="h-3.5 w-3.5" /></Button>
                    {p.status === "draft" && canEdit && (
                      <Button size="sm" variant="outline" onClick={() => sendPO(p)} data-testid={`send-po-${p.po_id}`}>
                        <Send className="h-3.5 w-3.5 mr-1" /> Send
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ReorderDialog open={reordering} onOpenChange={setReordering} suppliers={suppliers} />

      <POCreateDialog open={creating} onOpenChange={setCreating} items={items} suppliers={suppliers} />
      <POViewDialog po={viewing} onClose={() => setViewing(null)} canEdit={canEdit} />
    </div>
  );
}
